/**
 * 명세서 한 장의 **표시부**. 데이터는 밖에서 주입한다.
 *
 * 🔴 **기사앱과 담당자 시뮬레이터가 이 컴포넌트 하나를 공유한다**(2026-09-16).
 *    시뮬레이터용으로 비슷하게 베껴 만들면 그 순간부터 두 화면이 갈리고,
 *    "점검했는데 기사 화면은 다르더라"가 된다 — 점검 도구로서 의미가 사라진다.
 *    ⚠️ 그래서 이 파일은 API 도, 라우터도, 로그인도 모른다. props 만 받는다.
 *
 * 차인지급액은 **서버가 계산한 값**을 쓴다(금액을 클라에서 더하지 않는다 — 소수 4자리 왕복 사고).
 */

import { won } from './format';

const GROUPS = ['지급', '공제', '운행료'] as const;

/**
 * 금액 칸을 표시용으로만 좁힌다.
 *
 * ⚠️ 여기서 `Number(...)` 로 바꾸지 않는다 — 금액은 **문자열 그대로** 다뤄야 한다
 *    (pg numeric 을 숫자로 왕복시키면 소수 4자리가 깎인다). 타입만 좁히는 함수다.
 *    기사앱 API 는 `string | null`, 담당자 API 는 `unknown` 으로 같은 칸을 준다.
 */
const m = (v: unknown): string | number | null =>
  v === null || v === undefined ? null : (v as string | number);

export interface SlipLine {
  io?: string | null;
  category?: string | null;
  account_name?: string | null;
  amt?: unknown;
  comment?: string | null;
  car_id?: unknown;
  owner_id?: unknown;
}

export interface SlipSummary {
  plus_amt?: unknown;
  minus_amt?: unknown;
  result_amt?: unknown;
}

/** 차량·차주 묶음(월 중간 편성 변경 — 서버가 둘 이상일 때만 준다). 소계는 서버 값. */
export interface SlipGroup {
  car_id?: unknown;
  owner_id?: unknown;
  car_no?: string | null;
  owner_name?: string | null;
  plus_amt?: unknown;
  minus_amt?: unknown;
  result_amt?: unknown;
  period_from?: string | null;
  period_to?: string | null;
}

const key = (car: unknown, owner: unknown) => `${car ?? ''}|${owner ?? ''}`;
const md = (d: string | null | undefined) => (d && /^\d{4}-\d{2}-\d{2}$/.test(d) ? `${Number(d.slice(5, 7))}월 ${Number(d.slice(8, 10))}일` : null);

export default function SalarySlip({
  lines,
  summary,
  groups,
}: {
  lines: SlipLine[];
  /** 없으면 머리 카드를 안 그린다(합계를 화면에서 만들어 내지 않는다). */
  summary?: SlipSummary | null;
  /**
   * 그 달에 차량·차주가 둘 이상이면(월 중간 편성 변경 — 2026-10-02 way) 묶음마다 나눠 그린다.
   * 한 장에 옛 편성·새 편성 줄이 섞여 있으면 기사가 「어느 차·어느 차주 몫인지」를 모른다.
   */
  groups?: SlipGroup[] | null;
}) {
  const split = groups && groups.length > 1 ? groups : null;
  return (
    <>
      {summary && (
        <section className="mb-5 driver-card p-5">
          <div className="flex items-baseline justify-between">
            <span className="text-lg font-semibold text-ink">차인지급액</span>
            <span className="num text-2xl font-bold text-ink">{won(m(summary.result_amt))}원</span>
          </div>
          <div className="mt-3 flex justify-between text-base text-ink-soft">
            <span>지급 {won(m(summary.plus_amt))}원</span>
            <span>공제 {won(m(summary.minus_amt))}원</span>
          </div>
        </section>
      )}

      {split ? (
        <>
          <p className="mb-4 rounded-xl border border-amber-300 bg-amber-50 px-4 py-3 text-base text-amber-900">
            이번 달은 중간에 차량·차주가 바뀌어서 <b>{split.length}개로 나눠</b> 보여 드립니다. 위 차인지급액은 모두 더한 금액입니다.
          </p>
          {split.map((g, gi) => {
            const mine = lines.filter((l) => key(l.car_id, l.owner_id) === key(g.car_id, g.owner_id));
            const from = md(g.period_from);
            const to = md(g.period_to);
            return (
              <div key={gi} className="mb-6">
                <section className="mb-3 driver-card border-l-4 border-l-brand-600 p-4">
                  <div className="text-lg font-bold text-ink">
                    {g.car_no || '차량 없음'}{g.owner_name ? <span className="font-medium text-ink-soft"> · 차주 {g.owner_name}</span> : null}
                  </div>
                  {(from || to) && (
                    <div className="mt-0.5 text-base text-ink-soft">{from ?? ''}{from || to ? ' ~ ' : ''}{to ?? ''}</div>
                  )}
                  <div className="mt-2 flex justify-between text-base">
                    <span className="text-ink-soft">지급 {won(m(g.plus_amt))} · 공제 {won(m(g.minus_amt))}</span>
                    <span className="num font-bold text-ink">{won(m(g.result_amt))}원</span>
                  </div>
                </section>
                <Sections lines={mine} />
              </div>
            );
          })}
        </>
      ) : (
        <Sections lines={lines} />
      )}
    </>
  );
}

/**
 * 운행료 정보행 비고 끝의 「(수당:추가운행수당:385,000/운행수당:30,000)」 → 노선 설명과 수당 합.
 * 🔴 운행료 금액은 수당을 재는 기준이라 기사에게 보이지 않는다(10-15 way 긴급) — 금액 칸엔 수당 합만.
 */
export function splitAllowance(comment: string | null | undefined): { text: string; items: string[]; total: number | null } {
  const c = comment ?? '';
  const hit = /\s*\(수당:([^)]*)\)\s*$/.exec(c);
  if (!hit) return { text: c, items: [], total: null };
  const items = (hit[1] ?? '').split('/').map((s) => s.trim()).filter((s) => /\d/.test(s));
  const total = items.reduce((a, s) => a + Number((/([\d,]+)\s*$/.exec(s)?.[1] ?? '0').replace(/,/g, '')), 0);
  return { text: c.slice(0, hit.index), items, total: items.length ? total : null };
}

function Sections({ lines }: { lines: SlipLine[] }) {
  return (
    <>
      {GROUPS.map((g) => {
        const rows = lines.filter((l) => l.io === g);
        if (rows.length === 0) return null;
        return (
          <section key={g} className="mb-5">
            <h2 className="mb-2 text-lg font-bold text-ink">
              {/* `운행료` 는 지급이 아니라 **기록용 정보행**이라 합계에 안 들어간다(SPEC §4.3). */}
              {g === '운행료' ? '운행 수당 (참고)' : g}
            </h2>
            <ul className="driver-card divide-y divide-line">
              {g === '운행료' ? rows.map((l, i) => {
                const a = splitAllowance(l.comment);
                return (
                  <li key={i} className="flex items-center justify-between gap-3 px-4 py-3">
                    <span className="text-base text-ink">
                      {a.text || l.account_name || l.category || '-'}
                      {a.items.length ? <span className="block text-sm text-ink-faint">{a.items.join(' · ')}</span> : null}
                    </span>
                    <span className="num shrink-0 text-lg font-semibold text-ink">{a.total == null ? '—' : `${won(a.total)}원`}</span>
                  </li>
                );
              }) : rows.map((l, i) => (
                <li key={i} className="flex items-center justify-between gap-3 px-4 py-3">
                  <span className="text-base text-ink">
                    {l.account_name || l.category || '-'}
                    {l.comment ? <span className="block text-sm text-ink-faint">{l.comment}</span> : null}
                  </span>
                  <span className="num shrink-0 text-lg font-semibold text-ink">{won(m(l.amt))}원</span>
                </li>
              ))}
            </ul>
          </section>
        );
      })}
    </>
  );
}
